import { Table, Button, Popover, message, DatePicker } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import ViewButton from "../UI/ViewButton/viewbutton";
import DeleteCount from "./DeleteCount";
import CloseCount from './CloseCount';
import { CountInterface, CountDetailsInterfaceComplete } from "./Interfaces";
import LockButton from "../UI/LockButton/lockBtn";
import moment from "moment";
import { editCount, getCountListDetailsByID } from "../../api/inventoryCount";
import { useReactToPrint } from 'react-to-print';
import PrintButton from "../UI/PrintButton/printbutton";
import CountListPrint from "./CountListPrint";

type prop = {
  tableKey: number;
  counts: CountInterface[];
  countsSearch: CountInterface[];
  setCounts: React.Dispatch<React.SetStateAction<CountInterface[]>>;
  setCountsSearch: React.Dispatch<React.SetStateAction<CountInterface[]>>;
  isLoadingProp: boolean;
}

const CountList = ({ tableKey, counts, countsSearch, setCounts, isLoadingProp, setCountsSearch }: prop) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const [isCloseCountOpen, setIsCloseCountOpen] = useState(false);
  const [itemToClose, setItemToClose] = useState<{
    name: string,
    id: number
  }>({
    name: '',
    id: 0
  });
  const [isDeleteItemOpen, setIsDeleteItemOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    name: string,
    id: number
  }>({
    name: '',
    id: 0
  });
  const [selectedEditRow, setSelectedEditRow] = useState<number | undefined>();
  const [isLocked, setIsLocked] = useState(false)

  const [date, setDate] = useState("")

  const { mutateAsync: editCountMutation } = useMutation(
    "editCount",
    ({ id, date }: { id: number, date: string }) =>
      editCount({ id, date }),
    {
      onMutate: () => {
        message.loading('loading...')
      },
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
        setCounts(counts.map((item: CountInterface) => {
          if (item.id === data.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setCountsSearch(countsSearch.map((item: CountInterface) => {
          if (item.id === data.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setSelectedEditRow(undefined)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response.data.message)
      },
    }
  );

  const [countsDetails, setCountsDetails] = useState<CountDetailsInterfaceComplete | null>(null)

  const { mutateAsync: getCountsMutation, isLoading: isGetCountsLoading } = useMutation(
    "getCounts",
    ({ id }: { id: number }) =>
      getCountListDetailsByID({ id }),
    {
      onSuccess: ({ data }) => {
        message.destroy()
        message.success("printing successful")
        setCountsDetails(data)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response ? response.data.message : "error printing")
      },
    }
  );

  const handleDelete = (id: number, name: string): void => {
    setItemToDelete({ name, id });
    setIsDeleteItemOpen(true);
  };

  const handleClose = ({ id, name }: CountInterface) => {
    setItemToClose({ name, id })
    setIsCloseCountOpen(true)
  }

  const HandleEdit = (id: number, name: string) => {
    setSelectedEditRow(id)
  }

  const handleEditSave = (record: CountInterface, date: string) => {
    if (!date || record.date === date) {
      setSelectedEditRow(undefined)
      return
    }
    editCountMutation({ id: record.id, date })
  }

  const handleDatePicker = (date: any, stringDates: any) => {
    const parseDate = moment(stringDates).format('MM/DD/YYYY HH:mm:ss')
    setDate(parseDate)
  };

  const printComponentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });

  const handleClickPrint = (record: CountInterface) => {
    getCountsMutation({ id: record.id })
  }

  useEffect(() => {
    countsDetails?.data.length && handlePrint()
  }, [countsDetails])

  const columns = [
    {
      title: `${t("list-name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
    },
    {
      title: `${t("date")}`,
      dataIndex: "date",
      key: "date",
      width: "10rem",
      render: (date: string, record: CountInterface) => {
        return selectedEditRow === record.id && !(record.isClosed || isLocked) ?
          <DatePicker
            style={{ height: '30px' }}
            placeholder={`${t("start-date")}`}
            onChange={handleDatePicker}
            format="MM/DD/YYYY HH:mm:ss" />
          : <div style={{ display: 'flex', alignItems: "center", height: '30px' }}>{date}</div>
      }
    },
    {
      title: `${t("type")}`,
      dataIndex: "type",
      key: "type",
      width: "10rem",
      render: (type: any) => {
        return type
      },
    },
    {
      title: `${t("warehouse")}`,
      dataIndex: "warehouse",
      key: "warehouse",
      width: "10rem",
      render: (warehouse: any, record: CountInterface) => {
        return record.warehouse.name
      },
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "10rem",
      align: "center" as "center",
      render: (record: CountInterface) => (
        <>
          <CountListPrint contentRef={printComponentRef} countsDetails={countsDetails} />
          <PrintButton onClick={() => handleClickPrint(record)} />
          <ViewButton
            onClick={() => {
              const { id, name } = record
              navigate(`/inventory/count/details/${id}?title=${name}&canEdit=${!(record.isClosed || isLocked || !record.canDelete)}`);
            }}
          />
          {selectedEditRow === record.id && !record.isClosed ? <Popover content={t('confirm-changes')} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              className="Button"
              type="text"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "14px" }} />}
              onClick={() => handleEditSave(record, date)}
              size={"small"}

            />
          </Popover> : <EditButton disabled={record.isClosed || !record.canDelete} onClick={() => HandleEdit(record?.id, record?.name)} />}
          <LockButton
            disabled={!record.canDelete}
            isClosed={record.isClosed}
            onClick={() => handleClose(record)}
          />
          <DeleteButton
            disabled={!record.canDelete}
            onClick={() => {
              handleDelete(record?.id, record?.name);
            }}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <DeleteCount
        open={isDeleteItemOpen}
        setOpen={setIsDeleteItemOpen}
        itemToDelete={itemToDelete}
        counts={counts}
        countsSearch={countsSearch}
        setCounts={setCounts}
        setCountsSearch={setCountsSearch}
      />
      <Table
        scroll={{ x: 1000 }}
        key={tableKey}
        columns={columns}
        dataSource={counts}
        rowKey={(record) => record.id}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        loading={isLoadingProp || isGetCountsLoading}
      />
      <CloseCount
        itemToClose={itemToClose}
        isCloseCountOpen={isCloseCountOpen}
        setIsCloseCountOpen={setIsCloseCountOpen}
        setCounts={setCounts}
        setCountsSearch={setCountsSearch}
        counts={counts}
        countsSearch={countsSearch}
        setIsLocked={setIsLocked}
      />
    </>
  );
};

export default CountList;
